/**
 * A mechanic's shop (migration 0042): the shop owner — the mechanic an ISP
 * invited — adds his own techs, who then see the same shop board at every
 * ISP the shop works for. Only the owner picks the tech on a job and gets the
 * job notices. Cached on the device so the tech picker works with no signal.
 */
import { kvGet, kvSet } from '@routeback/core';
import { useCallback, useEffect, useState } from 'react';
import { useApp } from './app';
import { supabase } from './supabase';

export interface ShopTech { user_id: string; name: string; email: string }
export interface ShopInvite { id: string; email: string; name: string; code: string; expires_at: string }
export interface MyShop {
  role: 'owner' | 'tech';
  name: string;
  /** The shop owner's name. */
  owner: string;
  /** The companies (ISPs) the shop works for. */
  companies: string[];
  techs: ShopTech[];
  invites: ShopInvite[];
}

/** The shop's display name: what the owner called it, else his name. */
export function shopLabel(s: Pick<MyShop, 'name' | 'owner'>): string {
  return s.name.trim() || s.owner || 'Your shop';
}

/** Me → My shop and the tech picker. `null` = not a mechanic (or local mode); `undefined` = loading. */
export function useMyShop(): { shop: MyShop | null | undefined; reload: () => Promise<void> } {
  const app = useApp();
  const isMechanic = app.role === 'mechanic' && app.mode === 'cloud';
  const key = `my-shop:${app.userId}`;
  const [shop, setShop] = useState<MyShop | null | undefined>(isMechanic ? undefined : null);
  const reload = useCallback(async () => {
    if (!isMechanic || !supabase) { setShop(null); return; }
    const { data, error } = await supabase.rpc('my_shop');
    if (error) return;
    setShop((data as MyShop | null) ?? null);
    await kvSet(key, data ?? null);
  }, [isMechanic, key]);
  useEffect(() => {
    if (!isMechanic) { setShop(null); return; }
    let alive = true;
    void kvGet<MyShop | null>(key).then((c) => { if (alive && c !== undefined) setShop(c); });
    void reload();
    return () => { alive = false; };
  }, [isMechanic, key, reload]);
  return { shop, reload };
}

/** Run a shop RPC; returns the error message, or null. */
export async function shopRpc(fn: 'shop_rename' | 'shop_invite' | 'shop_cancel_invite' | 'shop_remove_tech' | 'shop_leave', args: Record<string, unknown> = {}): Promise<{ error: string | null; data: unknown }> {
  if (!supabase) return { error: 'Shops need the cloud.', data: null };
  const { data, error } = await supabase.rpc(fn, args);
  if (!error && fn === 'shop_invite') void supabase.functions.invoke('email-worker', { body: {} }).catch(() => undefined);
  return { error: error?.message ?? null, data };
}

/** Who the shop owner can put on a job: himself (no tech) or one of his techs. */
export function techChoices(shop: MyShop | null | undefined): ShopTech[] {
  return shop?.role === 'owner' ? shop.techs : [];
}
